/**
 * Auth0 SPA plugin for play-web.
 *
 *  - createAuth0() with localStorage caching + refresh tokens (with Safari/ITP fallback)
 *  - boot-time validator that fails fast if env vars are missing / TODO_*
 *  - centralised getAccessToken() for the axios interceptor 401-retry path
 *
 * The embedded iframe contract (parent injects ?userToken=) is handled
 * separately in main.ts — Auth0 is intentionally not registered as a Vue
 * plugin for embedded mode.
 */
import {
    createAuth0,
    type Auth0Plugin,
    type Auth0VueClient,
    type GetTokenSilentlyOptions,
} from '@auth0/auth0-vue';

import { env } from '@/env';
import { isAuthDisabled } from '@/store/auth/auth-utils';

const AUTH_LOCAL_STORAGE_PREFIX = '@@auth0spajs@@';
const REDIRECT_TARGET_KEY = 'auth_redirect_target';
const DEFAULT_TARGET = '/';

export type AuthCallbackError = { error: string; error_description: string };

/**
 * Captures Auth0 terminal redirect-callback errors from the URL once at module
 * load, before the Auth0 plugin's checkSession can strip `?error=&state=` via
 * `history.replaceState`.
 *
 * This is the narrow "terminal callback error" signal — unlike the SDK's shared
 * `auth.error` ref (set by ANY failed call, including silent token renewal), it
 * only reflects an actual rejected Auth0 redirect-callback. Control-flow gates
 * (router circuit breaker, session-wipe) must use this, not `auth.error`.
 *
 * Requires both `error` and `state` so unrelated `?error=` query params on
 * normal app routes are not treated as auth failures.
 */
export const captureAuthCallbackError = (): AuthCallbackError | null => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('error') && params.get('state')) {
        return {
            error: params.get('error') as string,
            error_description: params.get('error_description') || '',
        };
    }

    return null;
};

export const authCallbackError = captureAuthCallbackError();

export const clearAuth0LocalStorage = (): void => {
    Object.keys(localStorage)
        .filter((key) => key.startsWith(AUTH_LOCAL_STORAGE_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
};

type Auth0Config = {
    domain: string;
    clientId: string;
    audience: string;
};

const readAuth0Config = (): Auth0Config => {
    const config: Auth0Config = {
        domain: env.VUE_APP_AUTH_DOMAIN,
        clientId: env.VUE_APP_AUTH_CLIENT_ID,
        audience: env.VUE_APP_AUTH_AUDIENCE,
    };

    const missing = (Object.entries(config) as Array<[keyof Auth0Config, string]>)
        .filter(([, value]) => !value || String(value).startsWith('TODO_'))
        .map(([key]) => key);

    if (missing.length) {
        throw new Error(
            `Auth0 SPA application is not provisioned for env "${env.VUE_APP_ENV}": `
            + `missing or placeholder values for ${missing.join(', ')}. `
            + `Update .env.${env.VUE_APP_ENV || 'intg'} with the Auth0 domain, client_id, and audience.`,
        );
    }

    return config;
};

const isSafeRelativePath = (path: string): boolean => (
    path.startsWith('/') && !path.startsWith('//')
);

/**
 * Centralises the post-login redirect priority list. Used by the SDK's
 * onRedirectCallback so we don't need a dedicated /callback page — the SDK
 * auto-handles ?code=&state= on whatever URL it lands on, then we restore
 * the intended target via history.replaceState.
 *
 * Priority:
 *   1. appState.returnTo (set explicitly on loginWithRedirect)
 *   2. sessionStorage[auth_redirect_target] (set by the router guard / 401 retry)
 *   3. URL ?originalPath= or ?returnTo= (legacy deep-link query params)
 *   4. DEFAULT_TARGET
 */
export const resolveCallbackTarget = (appState?: Record<string, unknown>): string => {
    const fromAppState = appState?.returnTo as string | undefined
        ?? appState?.target as string | undefined;

    if (fromAppState) {
        return fromAppState;
    }

    const savedTarget = sessionStorage.getItem(REDIRECT_TARGET_KEY);

    if (savedTarget) {
        sessionStorage.removeItem(REDIRECT_TARGET_KEY);

        return savedTarget;
    }

    const url = new URL(window.location.href);

    const originalPath = url.searchParams.get('originalPath');

    if (originalPath) {
        const decoded = decodeURIComponent(originalPath);

        if (isSafeRelativePath(decoded)) return decoded;
    }

    const returnTo = url.searchParams.get('returnTo');

    if (returnTo) {
        const decoded = decodeURIComponent(returnTo);

        if (isSafeRelativePath(decoded)) return decoded;
    }

    return DEFAULT_TARGET;
};

const buildAuthClient = (): Auth0Plugin | null => {
    if (isAuthDisabled()) {
        return null;
    }

    const { domain, clientId, audience } = readAuth0Config();

    // @auth0/auth0-vue v2 removed `onRedirectCallback`. After exchanging the
    // ?code=&state= the SDK reads `appState.target` and either pushes it via
    // the registered Vue Router or replaces history with it. Our deep-link
    // restoration therefore happens via `appState.target` on every
    // loginWithRedirect call, plus the post-mount fallback in main.ts that
    // consumes sessionStorage when appState was lost (OAuth error, etc.).
    const client = createAuth0({
        domain,
        clientId,
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        // Falls back to silent-iframe auth when refresh-token cookies are
        // blocked (Safari ITP, strict-tracking-prevention browsers). Without
        // this, those users get a hard logout when the SDK can't rotate.
        useRefreshTokensFallback: true,
        authorizationParams: {
            audience,
            redirect_uri: window.location.origin,
            scope: 'openid profile email security_profile keap_profile offline_access',
        },
    }, {
        // Auth0 SDK reads ?error=&error_description= from the callback URL
        // and navigates here instead of continuing the auth flow. Paired
        // with the router-guard circuit breaker and the boot-time
        // authCallbackError signal (not the shared auth.error ref) to
        // prevent redirect loops when an Auth0 Action rejects the login
        // (e.g. Keap User Not Allowed, Locked Account).
        errorPath: '/auth-error',
    });

    const originalLogout = client.logout.bind(client);

    client.logout = ((options?: Parameters<Auth0VueClient['logout']>[0]) => {
        clearAuth0LocalStorage();
        // Explicit logout invalidates any pending deep-link restore so the
        // next login lands on the default target, not the user's last
        // router-guard-visited page.
        sessionStorage.removeItem(REDIRECT_TARGET_KEY);

        return originalLogout(options);
    }) as Auth0VueClient['logout'];

    return client;
};

export const auth: Auth0Plugin | null = buildAuthClient();

/**
 * One-shot fallback to restore a deep link from sessionStorage when the SDK's
 * `appState.target` flow didn't run (e.g. the user manually visited the bare
 * origin while a `auth_redirect_target` was still queued). Call from main.ts
 * after `app.use(auth)`.
 */
export const consumeQueuedRedirectTarget = (): void => {
    if (!auth) return;

    const saved = sessionStorage.getItem(REDIRECT_TARGET_KEY);

    if (saved && saved !== window.location.pathname + window.location.search) {
        sessionStorage.removeItem(REDIRECT_TARGET_KEY);
        window.history.replaceState({}, document.title, saved);
    }
};

/**
 * Single async access-token getter. Maps `ignoreCache` to the SDK's
 * `cacheMode: 'off'` so callers (axios interceptor, etc.) can force a fresh
 * token on 401 retry without exposing the raw SDK option to every consumer.
 *
 * Returns `undefined` when Auth0 is not initialized (auth-disabled / embed
 * mode), so callers can safely `await getAccessToken()` regardless.
 */
export async function getAccessToken(
    { ignoreCache = false }: { ignoreCache?: boolean } = {},
): Promise<string | undefined> {
    if (!auth) {
        return undefined;
    }

    const options: GetTokenSilentlyOptions | undefined = ignoreCache
        ? { cacheMode: 'off' }
        : undefined;

    return auth.getAccessTokenSilently(options);
}

/**
 * Centralised logout entry point. Calls `auth.logout()` directly so the SDK
 * can perform the redirect to `/v2/logout` without contention from the Vue
 * Router or the SDK's own `__checkSession` restoring the previous deep link
 * on a full page reload.
 */
export function performLogout(): void {
    if (!auth) return;

    clearAuth0LocalStorage();
    auth.logout({
        logoutParams: { returnTo: window.location.origin },
    });
}
